import { attr, belongsTo, hasMany } from '@ember-data/model';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { A as emberA } from '@ember/array';
import { roundToCurrency } from 'client-portal/helpers/format-money';
import AddressableMixin from 'client-portal/mixins/polymorphic/addressable';
import PhoneableMixin from 'client-portal/mixins/polymorphic/phoneable';
import Super from './base-client';

export default class TreatableClient extends Super.extend(PhoneableMixin, AddressableMixin) {
  @belongsTo('client-billing-overview', { async: false, inverse: null }) clientBillingOverview;
  @belongsTo('prescreener-answer', { async: false, inverse: null }) prescreenerAnswer;

  @hasMany('invoice', { async: false, inverse: null }) unpaidInvoices;
  @hasMany('card', { async: false, inverse: null }) cards;
  @hasMany('stripe-ach-bank-account', { async: false, inverse: null }) stripeAchBankAccounts;
  @hasMany('appointment', { async: false, inverse: null }) uninvoicedAppointments;
  @hasMany('monarch-channel-client-info', {
    async: false,
    inverse: 'client',
    as: 'treatable-client',
  })
  monarchChannelClientInfos;

  @attr('boolean') hasIncompleteDocument;
  @attr() supervisorInfo;
  @attr('boolean') hasInvoicedAppointments;
  @attr('boolean') latestChannelHasBillingBannerEnabled;
  @attr('string') latestChannelName;

  // Virtual
  @attr() registrationSource;
  @attr() channelCustomFields;

  // Keep as a classic @computed so payment-methods'
  // `@reads('model.defaultCard')` re-fires when another card's `isDefault`
  // flips. @dependentKeyCompat on a plain getter does not propagate through
  // `@each.isDefault` for classic macros.
  @computed('cards.@each.isDefault')
  get defaultCard() {
    return this.cards.find(({ isDefault }) => isDefault);
  }

  @computed('unpaidInvoices.@each.isChannelBilled')
  get visibleUnpaidInvoices() {
    return this.unpaidInvoices.filter(invoice => !invoice.isChannelBilled);
  }

  @computed(
    'clientBillingOverview.balanceDue',
    'unpaidInvoices.@each.{isChannelBilled,remainingAmount,invoiceDate}'
  )
  get visibleBalanceDue() {
    let channelRemaining = this.unpaidInvoices
      .filter(invoice => invoice.isChannelBilled && !invoice.invoiceDate?.isAfter())
      .reduce((sum, invoice) => sum + (invoice.remainingAmount || 0), 0);

    return roundToCurrency((this.clientBillingOverview?.balanceDue || 0) - channelRemaining);
  }

  static fieldsFor(context) {
    let fields = emberA();
    this.fields.forEach((_, v) => fields.pushObject(v));
    fields.removeObject('meta');
    fields.removeObjects([
      'uninvoicedAppointments',
      'registrationSource',
      'channelCustomFields',
      'prescreenerAnswer',
      'isMinor',
    ]);

    switch (context) {
      case 'environment':
        fields.removeObjects(['clientRelationships', 'genderInfo', 'prescreenerAnswer']);
        break;
    }

    return fields;
  }
}
